import { api } from "../scripts/api.js";
import { PUBLIC_AREAS, PRIVATE_AREAS, AREA_ORDER, loadTrainingAreas } from "../scripts/training-content.js";

/*
   <app-access>: who sees which Training area.                    (2026-10-08)

   admin.html#access. A grid of AUDIENCES (rows) against Training AREAS
   (columns): tick a box and that audience sees that area on its next page
   load. Under the grid, who is in each audience (email domains, single emails,
   classes), a form for a new audience (a new school), and a "check one person"
   lookup.

   Server: routes/content-access-admin.js (admins only). Same tables as
   server/set-audiences.js. Design: CONTENT-ACCESS-AUDIENCES-DESIGN-2026-10-08.md.

   Area titles come from the Training registry itself (an admin is allowed every
   chunk), so a renamed area is renamed here too. A grant for a slug the portal
   does not know yet (a school's area whose file has not shipped) still gets a
   column, labelled with its slug, so it can be seen and unticked.
*/

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

const REASONS = {
   already_exists: "That is already there.",
   invalid_value: "That does not look right. Domains look like arizona.edu, emails like name@school.edu, and names use lower-case letters, numbers and dashes.",
   no_such_class: "That class does not exist.",
   no_such_audience: "That audience no longer exists. Reload the page.",
   audiences_not_installed: "The audience tables are not there yet. Run the 2026-10-08 migration first.",
   not_authorized: "Only admins can change access.",
};
const reason = (r, fallback) => REASONS[r] || fallback || `Something went wrong (${r}).`;

const KIND_LABEL = { domain: "Email domain", email: "One email", class: "Class" };

class AppAccess extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <div class="card">
            <h2 style="margin:0">Training access</h2>
            <p class="muted" style="margin:6px 0 0">
               Tick a box to let an audience see a Training area. Students pick the change up the next
               time they load the portal. Professors and admins always see everything, and signing in
               still needs a roster: an audience only decides what a signed-in student sees.
            </p>
         </div>
         <div class="card">
            <div class="acc-head">
               <h3 style="margin:0">Who sees what</h3>
               <span id="acc-status" class="msg" style="margin:0"></span>
            </div>
            <div id="acc-grid" class="acc-scroll"><p class="muted">Loading…</p></div>
            <p class="muted acc-foot">
               The Competition Case Library is also open to any student in a class whose library switch
               is on, whatever their audience. Skill Guides are public and need no box.
            </p>
         </div>
         <div class="card">
            <h3 style="margin:0 0 4px">Who is in each audience</h3>
            <p class="muted" style="margin:0 0 12px">
               A student is in an audience when any line matches them. A domain also covers its
               subdomains (arizona.edu covers email.arizona.edu).
            </p>
            <div id="acc-people"></div>
         </div>
         <div class="card">
            <h3 style="margin:0 0 4px">New audience</h3>
            <p class="muted" style="margin:0 0 12px">For another school or team. Add who is in it and tick its areas afterwards.</p>
            <div class="acc-form">
               <div><label for="acc-new-name">Name</label><input id="acc-new-name" placeholder="Texas Tech"></div>
               <div><label for="acc-new-slug">Short id</label><input id="acc-new-slug" placeholder="ttu"></div>
               <div class="acc-form-btn"><button type="button" id="acc-new-go">Create</button></div>
            </div>
            <span id="acc-new-msg" class="msg"></span>
         </div>
         <div class="card">
            <h3 style="margin:0 0 4px">Check one person</h3>
            <p class="muted" style="margin:0 0 12px">See exactly what a student sees, by the email they sign in with.</p>
            <div class="acc-form">
               <div class="acc-grow"><label for="acc-who-email">Email</label><input id="acc-who-email" placeholder="student@arizona.edu"></div>
               <div class="acc-form-btn"><button type="button" id="acc-who-go">Check</button></div>
            </div>
            <div id="acc-who-out" class="acc-who"></div>
         </div>`;

      this.querySelector("#acc-new-go").addEventListener("click", () => this.create());
      this.querySelector("#acc-new-name").addEventListener("input", (e) => {
         const slug = this.querySelector("#acc-new-slug");
         if (!slug.dataset.touched) slug.value = e.target.value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
      });
      this.querySelector("#acc-new-slug").addEventListener("input", (e) => { e.target.dataset.touched = "1"; });
      this.querySelector("#acc-who-go").addEventListener("click", () => this.who());
      this.querySelector("#acc-who-email").addEventListener("keydown", (e) => { if (e.key === "Enter") this.who(); });
      this.load();
   }

   show() { this.load(); }

   status(text, kind = "") {
      const el = this.querySelector("#acc-status");
      if (!el) return;
      el.textContent = text;
      el.className = `msg${kind ? " " + kind : ""}`;
      clearTimeout(this._statusTimer);
      if (kind === "ok") this._statusTimer = setTimeout(() => { el.textContent = ""; }, 2500);
   }

   async areaColumns(state) {
      if (!this._areaTitles) {
         const loaded = await loadTrainingAreas({ all: true });
         this._areaTitles = Object.fromEntries(loaded.map((a) => [a.slug, a.title]));
      }
      const publicSlugs = PUBLIC_AREAS.map((a) => a.slug);
      const known = AREA_ORDER.filter((s) => PRIVATE_AREAS[s]);
      const extra = [...new Set(state.audiences.flatMap((a) => a.grants))]
         .filter((s) => !known.includes(s) && !publicSlugs.includes(s)).sort();
      return {
         cols: [
            ...known.map((s) => ({ slug: s, title: this._areaTitles[s] || s, known: true })),
            ...extra.map((s) => ({ slug: s, title: s, known: false })),
         ],
         publicCols: PUBLIC_AREAS.map((a) => ({ slug: a.slug, title: a.title })),
      };
   }

   async load() {
      const grid = this.querySelector("#acc-grid");
      try {
         const res = await api("/admin/access");
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || res.status);
         this.state = data;
         const { cols, publicCols } = await this.areaColumns(data);
         this.cols = cols;
         this.renderGrid(cols, publicCols);
         this.renderPeople();
      } catch (e) {
         grid.innerHTML = `<p class="msg err">${esc(reason(e.message, `Could not load access (${e.message}).`))}</p>`;
      }
   }

   renderGrid(cols, publicCols) {
      const grid = this.querySelector("#acc-grid");
      const auds = this.state.audiences;
      if (!auds.length) {
         grid.innerHTML = `<p class="muted">No audiences yet. Create one below.</p>`;
         return;
      }
      const head = `<tr><th class="acc-aud">Audience</th>
         ${cols.map((c) => `<th>${esc(c.title)}${c.known ? "" : `<span class="acc-tag">not in the portal yet</span>`}</th>`).join("")}
         ${publicCols.map((c) => `<th class="acc-public">${esc(c.title)}</th>`).join("")}</tr>`;
      const rows = auds.map((a) => `<tr>
         <th class="acc-aud" scope="row"><span class="acc-name">${esc(a.name)}</span>
            <span class="acc-sub">${esc(a.slug)} · ${a.user_count} ${a.user_count === 1 ? "person" : "people"}</span></th>
         ${cols.map((c) => `<td><label class="acc-box" title="${esc(a.name)}: ${esc(c.title)}">
            <input type="checkbox" data-aud="${esc(a.slug)}" data-area="${esc(c.slug)}" ${a.grants.includes(c.slug) ? "checked" : ""}>
            <span class="acc-sr">${esc(a.name)} sees ${esc(c.title)}</span></label></td>`).join("")}
         ${publicCols.map(() => `<td class="acc-public"><span class="acc-always">Public</span></td>`).join("")}
      </tr>`).join("");
      grid.innerHTML = `<table class="acc-table"><thead>${head}</thead><tbody>${rows}</tbody></table>`;
      grid.querySelectorAll('input[type="checkbox"]').forEach((box) =>
         box.addEventListener("change", () => this.toggle(box)));
   }

   async toggle(box) {
      const granted = box.checked;
      const { aud, area } = box.dataset;
      box.disabled = true;
      this.status("Saving…");
      try {
         const res = await api("/admin/access/grant", {
            method: "PUT",
            body: JSON.stringify({ audience: aud, area, granted }),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || res.status);
         const a = this.state.audiences.find((x) => x.slug === aud);
         if (a) a.grants = granted ? [...new Set([...a.grants, area])] : a.grants.filter((g) => g !== area);
         const title = (this.cols.find((c) => c.slug === area) || {}).title || area;
         this.status(`${a ? a.name : aud} ${granted ? "now sees" : "no longer sees"} ${title}.`, "ok");
      } catch (e) {
         box.checked = !granted; // put the box back the way the server has it
         this.status(reason(e.message, `Not saved (${e.message}).`), "err");
      } finally {
         box.disabled = false;
      }
   }

   renderPeople() {
      const host = this.querySelector("#acc-people");
      const classOpts = this.state.classes
         .map((c) => `<option value="${c.class_id}">${esc(c.label || `Class ${c.class_id}`)} (id ${c.class_id})</option>`).join("");
      host.innerHTML = this.state.audiences.map((a) => `
         <div class="acc-group" data-aud="${esc(a.slug)}">
            <div class="acc-group-head">
               <strong>${esc(a.name)}</strong> <span class="acc-sub">${esc(a.slug)}</span>
               <button type="button" class="ghost acc-del" data-del-aud="${esc(a.slug)}">Delete audience</button>
            </div>
            ${a.description ? `<p class="muted acc-desc">${esc(a.description)}</p>` : ""}
            <ul class="acc-rules">
               ${a.rules.length ? a.rules.map((r) => `<li>
                  <span class="acc-kind">${esc(KIND_LABEL[r.kind] || r.kind)}</span>
                  <span class="acc-rule-val">${esc(r.kind === "class" ? `${r.class_label || ""} (id ${r.class_id})` : r.value)}</span>
                  <button type="button" class="ghost acc-x" data-rule="${r.rule_id}" aria-label="Remove">Remove</button>
               </li>`).join("") : `<li class="muted">Nobody yet. Add a domain, an email or a class below.</li>`}
            </ul>
            <div class="acc-form">
               <div><label>Add</label>
                  <select class="acc-kind-pick">
                     <option value="domain">Email domain</option>
                     <option value="email">One email</option>
                     <option value="class">Class</option>
                  </select></div>
               <div class="acc-grow acc-val"><label class="acc-spacer" aria-hidden="true">&nbsp;</label><input class="acc-value" placeholder="school.edu"></div>
               <div class="acc-grow acc-cls" hidden><label class="acc-spacer" aria-hidden="true">&nbsp;</label><select class="acc-class">${classOpts}</select></div>
               <div class="acc-form-btn"><button type="button" class="acc-add">Add</button></div>
            </div>
            <span class="msg acc-msg"></span>
         </div>`).join("");

      host.querySelectorAll(".acc-group").forEach((g) => {
         const pick = g.querySelector(".acc-kind-pick");
         const sync = () => {
            const isClass = pick.value === "class";
            g.querySelector(".acc-val").hidden = isClass;
            g.querySelector(".acc-cls").hidden = !isClass;
            g.querySelector(".acc-value").placeholder = pick.value === "email" ? "student@gmail.com" : "school.edu";
         };
         pick.addEventListener("change", sync);
         sync();
         g.querySelector(".acc-add").addEventListener("click", () => this.addRule(g));
         g.querySelector(".acc-value").addEventListener("keydown", (e) => { if (e.key === "Enter") this.addRule(g); });
         g.querySelectorAll("[data-rule]").forEach((b) => b.addEventListener("click", () => this.removeRule(g, b.dataset.rule)));
         g.querySelector("[data-del-aud]").addEventListener("click", () => this.deleteAudience(g.dataset.aud));
      });
   }

   async addRule(g) {
      const msg = g.querySelector(".acc-msg");
      const kind = g.querySelector(".acc-kind-pick").value;
      const body = { audience: g.dataset.aud, kind };
      if (kind === "class") body.class_id = Number(g.querySelector(".acc-class").value);
      else {
         body.value = g.querySelector(".acc-value").value.trim();
         if (!body.value) { msg.textContent = "Type a domain or an email first."; msg.className = "msg err acc-msg"; return; }
      }
      msg.textContent = "Adding…"; msg.className = "msg acc-msg";
      try {
         const res = await api("/admin/access/rules", { method: "POST", body: JSON.stringify(body) });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || res.status);
         await this.load();
      } catch (e) {
         msg.textContent = reason(e.message, `Not added (${e.message}).`);
         msg.className = "msg err acc-msg";
      }
   }

   async removeRule(g, ruleId) {
      const msg = g.querySelector(".acc-msg");
      try {
         const res = await api(`/admin/access/rules/${encodeURIComponent(ruleId)}`, { method: "DELETE" });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || res.status);
         await this.load();
      } catch (e) {
         msg.textContent = reason(e.message, `Not removed (${e.message}).`);
         msg.className = "msg err acc-msg";
      }
   }

   async deleteAudience(slug) {
      const a = this.state.audiences.find((x) => x.slug === slug);
      if (!window.confirm(`Delete "${a ? a.name : slug}"? Everyone in it loses the areas it grants. This cannot be undone.`)) return;
      try {
         const res = await api(`/admin/access/audiences/${encodeURIComponent(slug)}`, { method: "DELETE" });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || res.status);
         this.status(`Deleted ${a ? a.name : slug}.`, "ok");
         await this.load();
      } catch (e) {
         this.status(reason(e.message, `Not deleted (${e.message}).`), "err");
      }
   }

   async create() {
      const msg = this.querySelector("#acc-new-msg");
      const name = this.querySelector("#acc-new-name").value.trim();
      const slug = this.querySelector("#acc-new-slug").value.trim().toLowerCase();
      if (!name || !slug) { msg.textContent = "Give it a name and a short id."; msg.className = "msg err"; return; }
      msg.textContent = "Creating…"; msg.className = "msg";
      try {
         const res = await api("/admin/access/audiences", { method: "POST", body: JSON.stringify({ slug, name }) });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || res.status);
         msg.textContent = `Created ${name}. Add who is in it below, then tick its areas.`; msg.className = "msg ok";
         this.querySelector("#acc-new-name").value = "";
         const s = this.querySelector("#acc-new-slug"); s.value = ""; delete s.dataset.touched;
         await this.load();
      } catch (e) {
         msg.textContent = reason(e.message, `Not created (${e.message}).`); msg.className = "msg err";
      }
   }

   async who() {
      const out = this.querySelector("#acc-who-out");
      const email = this.querySelector("#acc-who-email").value.trim();
      if (!email) return;
      out.textContent = "Checking…";
      try {
         const res = await api(`/admin/access/who?email=${encodeURIComponent(email)}`);
         const d = await res.json();
         if (!res.ok || !d.ok) throw new Error(d.reason || res.status);
         if (!d.found) { out.innerHTML = `<p><strong>${esc(email)}</strong> has no account, so they cannot sign in at all. Add them to a class roster first.</p>`; return; }
         if (d.all) { out.innerHTML = `<p><strong>${esc(d.email)}</strong> is a ${esc(d.role)} and sees every area.</p>`; return; }
         const titles = [...PUBLIC_AREAS.map((a) => a.title),
            ...d.areas.map((s) => (this._areaTitles && this._areaTitles[s]) || s)];
         if (d.library) titles.push((this._areaTitles && this._areaTitles["case-library"]) || "Case library");
         out.innerHTML = `<p><strong>${esc(d.email)}</strong> is in ${d.audiences.length
            ? d.audiences.map((s) => `<span class="acc-chip">${esc(s)}</span>`).join(" ")
            : "no audience"}.</p><p>Sees: ${titles.map(esc).join(", ")}.</p>`;
      } catch (e) {
         out.textContent = reason(e.message, `Could not check (${e.message}).`);
      }
   }
}

customElements.define("app-access", AppAccess);
